import styled from '@emotion/styled';
import { Alert, Divider, Stack, tokens } from '@hotjar/hotsauce';
import { useTranslationWithPrefix } from '@hotjar/translations';
import type { Context } from '../../../state-machine/checkout.machine.types';
import { Heading } from '../EditOrderContainer.styles';
import { CostChangesSummary } from '../cost-changes/cost-changes-summary/CostChangesSummary';
import type { OrganizationListItem } from '~/subscription-management/features/service-resource-list/hooks/useServiceResourceList';
import { PaymentInfoCard } from './billing-details/PaymentInfoCard';
import { InvoiceDetailsReadonly } from '~/subscription-management/components';
import { PriceReviewNextRecurring, PriceReviewTodayCharge } from './PriceReviewHelpers';
import type { DiscountsType } from '~/subscription-management/components/discount/Discounts';

interface Props {
  context: Context;
  loading: boolean;
  allResources?: OrganizationListItem[];
  accountDiscounts: DiscountsType[];
  nextChargeDate?: string;
  editSubscriptions: () => void;
  navigateToInvoiceDetails: () => void;
  navigateToPaymentDetails: () => void;
}

const S = {
  Divider: styled(Divider)`
    margin: ${tokens.spacer.x4} 0;
    border-color: ${tokens.ALIAS_COLORS.ui_border};
  `,
  Title: styled.h2`
    ${tokens.text('subtitle1')};
    margin-bottom: ${tokens.spacer.x2};
  `,
  BillingDetails: styled.div`
    display: grid;
    grid-template-columns: 50% 50%;
    gap: ${tokens.spacer.x2};
  `,
  Alert: styled(Alert)`
    margin-bottom: ${tokens.spacer.x3};
  `,
  InvoiceDetailsReadonly: styled(InvoiceDetailsReadonly)`
    width: fit-content;
  `,
};

export function ReviewOrder({
  context: {
    costs,
    hasActiveSubscription,
    allowancesToAdd: updatedProducts,
    billingPeriod,
    customer,
    hasPaymentOrderFailure,
    isChurning,
  },
  loading,
  allResources,
  accountDiscounts,
  nextChargeDate,
  editSubscriptions,
  navigateToInvoiceDetails,
  navigateToPaymentDetails,
}: Props): JSX.Element {
  const { t: tReviewOrder } = useTranslationWithPrefix('checkout:wizard.review-order');
  const { t: tHeader } = useTranslationWithPrefix('checkout:header');
  const { t: tBillingDetails } = useTranslationWithPrefix(
    'checkout:wizard.review-order.billing-details',
  );

  return (
    <>
      <Heading as="h1" textType="header4">
        {tHeader('review-order')}
      </Heading>

      {hasPaymentOrderFailure && (
        <S.Alert title={tReviewOrder('order-failed.title')} kind="danger">
          {tReviewOrder('order-failed.message')}
        </S.Alert>
      )}

      <Stack spacing="x2" direction="column">
        {hasActiveSubscription && !isChurning && (
          <PriceReviewTodayCharge
            currency={customer.currency}
            payNowAmountWithTax={costs.payNow?.amountWithTax ?? 0}
            taxAmount={costs.renewWith?.taxAmount}
            loading={loading}
          />
        )}

        <PriceReviewNextRecurring
          billingPeriod={billingPeriod}
          currency={customer.currency}
          taxAmount={costs.renewWith?.taxAmount}
          renewWithAmountWithTax={costs.renewWith?.amountWithTax ?? 0}
          loading={loading}
          accountDiscounts={accountDiscounts}
          nextChargeDate={nextChargeDate}
        />

        {!!allResources && (
          <CostChangesSummary
            allResources={allResources}
            updatedProducts={updatedProducts}
            billingPeriod={billingPeriod}
            currency={customer.currency}
            defaultExpanded={isChurning}
            isChurning={isChurning}
            edit={editSubscriptions}
          />
        )}
      </Stack>

      {!isChurning && (
        <>
          <S.Divider />

          <S.Title>{tBillingDetails('title')}</S.Title>
          <S.BillingDetails>
            <S.InvoiceDetailsReadonly
              customer={customer}
              onEdit={navigateToInvoiceDetails}
              displayEditBelow
            />

            {customer.paymentMethod ? (
              <PaymentInfoCard
                paymentMethod={customer.paymentMethod}
                onEdit={navigateToPaymentDetails}
              />
            ) : null}
          </S.BillingDetails>
        </>
      )}
    </>
  );
}
